import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewTiedoteMutation } from '../../api/tiedotteetApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function TiedoteList({ tiedotteet, handleDeleteTiedote, handleUpdateTiedote, fetchData, onSuccess, onError }) {
    const [selectedTiedote, setSelectedTiedote] = useState(null);
    const [newTiedoteData, setNewTiedoteData] = useState({ otsikko: '', tiedote: '', etusivu: false });
    const [addTiedoteData, setAddTiedoteData] = useState({ otsikko: '', tiedote: '', etusivu: false });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;
    const [addNewTiedote, { isLoading }] = useAddNewTiedoteMutation();

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredTiedotteet = Array.isArray(tiedotteet)
        ? tiedotteet.filter((tiedote) =>
            tiedote.otsikko.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
    const totalPages = Math.ceil(filteredTiedotteet.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedTiedotteet = filteredTiedotteet.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (tiedote) => {
        setShowAddForm(false);
        if (selectedTiedote?.id === tiedote.id) {
            setSelectedTiedote(null);
            setNewTiedoteData({ otsikko: '', tiedote: '', etusivu: false });
        } else {
            setSelectedTiedote(tiedote);
            setNewTiedoteData(tiedote);
        }
    };

    const handleAddClick = () => {
        setSelectedTiedote(null);
        setNewTiedoteData({ otsikko: '', tiedote: '', etusivu: false });
        setShowAddForm(!showAddForm);
    };

    const handleLisääTiedote = async (tiedote) => {
        try {
            const result = await addNewTiedote(tiedote);
            if (result.error) onError('Tiedotteen luominen epäonnistui');
            if (!result.error) onSuccess('Tiedotteen luominen onnistui.', 'tiedotteet', result.data.id);
            setAddTiedoteData({ otsikko: '', tiedote: '', etusivu: false });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Tiedotteen luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Tiedotteet
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Tiedotteet List */}
                {displayedTiedotteet.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei tiedotteita näytettäväksi.
                    </Typography>
                ) : (
                    displayedTiedotteet.map((tiedote) => {
                        const isSelected = selectedTiedote?.id === tiedote.id;
                        const previewText = tiedote.tiedote?.replace(/\s+/g, ' ').trim();

                        return (
                            <Box key={tiedote.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(tiedote)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                                            {tiedote.otsikko}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {previewText ? `${previewText.slice(0, 120)}${previewText.length > 120 ? '...' : ''}` : 'Ei sisältöä'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {tiedote.etusivu && <Chip size="small" label="Etusivu" />}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteTiedote(event, tiedote);
                                            if (selectedTiedote?.id === tiedote.id) {
                                                setSelectedTiedote(null);
                                                setNewTiedoteData({ otsikko: '', tiedote: '', etusivu: false });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa tiedotetta
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="otsikko"
                                                    label="Otsikko"
                                                    multiline
                                                    fullWidth
                                                    value={newTiedoteData.otsikko}
                                                    onChange={(event) =>
                                                        setNewTiedoteData((prev) => ({ ...prev, otsikko: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormTextField
                                                    fieldName="tiedote"
                                                    label="Tiedote"
                                                    multiline
                                                    fullWidth
                                                    rows={8}
                                                    value={newTiedoteData.tiedote}
                                                    onChange={(event) =>
                                                        setNewTiedoteData((prev) => ({ ...prev, tiedote: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={newTiedoteData.etusivu}
                                                            onChange={(event) =>
                                                                setNewTiedoteData((prev) => ({ ...prev, etusivu: event.target.checked }))
                                                            }
                                                            name="etusivu"
                                                            color="primary"
                                                        />
                                                    }
                                                    label="Näytä etusivulla"
                                                    sx={{ mb: 2 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateTiedote} data={newTiedoteData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää tiedote'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi tiedote
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addTiedoteOtsikko"
                                        fieldName="otsikko"
                                        label="Otsikko"
                                        multiline
                                        fullWidth
                                        value={addTiedoteData.otsikko}
                                        onChange={(event) =>
                                            setAddTiedoteData((prev) => ({ ...prev, otsikko: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormTextField
                                        key="addTiedoteTiedote"
                                        fieldName="tiedote"
                                        label="Tiedote"
                                        multiline
                                        fullWidth
                                        rows={8}
                                        value={addTiedoteData.tiedote}
                                        onChange={(event) =>
                                            setAddTiedoteData((prev) => ({ ...prev, tiedote: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={addTiedoteData.etusivu}
                                                onChange={(event) =>
                                                    setAddTiedoteData((prev) => ({ ...prev, etusivu: event.target.checked }))
                                                }
                                                name="etusivu"
                                                color="primary"
                                            />
                                        }
                                        label="Näytä etusivulla"
                                        sx={{ mb: 2 }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääTiedote(addTiedoteData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredTiedotteet.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredTiedotteet}
                            array={tiedotteet}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default TiedoteList;